import { type PropsWithChildren } from "react"
import { Container, Grid, GridItem, Visibility } from "@octopus-energy/coral/atoms"
import { PageNavigation, PageNavigationItem } from "@octopus-energy/coral/molecules"
import { useQuery } from "@tanstack/react-query"
import { type CookieValueTypes } from "cookies-next"
import { assertWithErrorCode } from "@krakentech/error-intl"
import { ConstantineAmbient } from "@/components/atoms"
import { ERROR_CODE } from "@/constants/errorCode"
import { graphql } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { useDashboardNavigation } from "../../../hooks/useDashboardNavigation"
import {
    DESKTOP_TEMPLATE_AREAS,
    DESKTOP_TEMPLATE_COLUMNS,
    DESKTOP_TEMPLATE_ROWS,
    MOBILE_TEMPLATE_AREAS,
    MOBILE_TEMPLATE_COLUMNS,
    TABLET_TEMPLATE_AREAS,
    TABLET_TEMPLATE_COLUMNS,
    TABLET_TEMPLATE_ROWS,
} from "../constants/styles"
import { DashboardLayoutBreadcrumbContextProvider } from "../contexts/DashboardLayoutBreadcrumbContext"
import { type TransformBreadcrumb } from "../types/TransformBreadcrumb"
import { DashboardLayoutContainer } from "./DashboardLayoutContainer"

const DashboardLayoutNestedQuery = graphql(
    `
        query DashboardLayoutNestedQuery {
            viewer {
                ...DashboardLayoutBreadcrumbContext_AccountUser
            }
        }
    `,
    [DashboardLayoutBreadcrumbContextProvider.fragments.accountUser]
)

type DashboardLayoutNestedProps = PropsWithChildren<{
    transformBreadcrumb?: TransformBreadcrumb
    lastDashboardNavigationCookie?: CookieValueTypes
}>

export function DashboardLayoutNested({
    children,
    transformBreadcrumb,
    lastDashboardNavigationCookie,
}: DashboardLayoutNestedProps) {
    const graphQLClient = useGraphQLClient()
    const navigationItems = useDashboardNavigation(lastDashboardNavigationCookie)

    const viewerResult = useQuery({
        queryKey: ["viewer"],
        queryFn: async () => {
            const result = await graphQLClient.request(DashboardLayoutNestedQuery)

            assertWithErrorCode(result.viewer, ERROR_CODE.UNAUTHORIZED)

            return result.viewer
        },
    })

    // TODO: how should we handle loading state?
    if (!viewerResult.data) {
        return null
    }

    return (
        <DashboardLayoutBreadcrumbContextProvider
            user={viewerResult.data}
            transformBreadcrumb={transformBreadcrumb}
            lastDashboardNavigationCookie={lastDashboardNavigationCookie}
        >
            <DashboardLayoutContainer>
                <Grid
                    fullWidth
                    columnGap="sm"
                    rowGap="lg"
                    gridTemplateAreas={{
                        base: MOBILE_TEMPLATE_AREAS,
                        lg: TABLET_TEMPLATE_AREAS,
                        xl: DESKTOP_TEMPLATE_AREAS,
                    }}
                    gridTemplateColumns={{
                        base: MOBILE_TEMPLATE_COLUMNS,
                        lg: TABLET_TEMPLATE_COLUMNS,
                        xl: DESKTOP_TEMPLATE_COLUMNS,
                    }}
                    gridTemplateRows={{
                        base: undefined,
                        lg: TABLET_TEMPLATE_ROWS,
                        xl: DESKTOP_TEMPLATE_ROWS,
                    }}
                >
                    <GridItem
                        gridArea="constantine"
                        justifySelf="center"
                        alignSelf="center"
                    >
                        <ConstantineAmbient />
                    </GridItem>
                    <GridItem gridArea="nav">
                        <Container
                            position="sticky"
                            top={100}
                        >
                            <Visibility hidden={{ base: true, lg: false }}>
                                <PageNavigation>
                                    {navigationItems.map((item) => {
                                        const Icon = item.icon

                                        return (
                                            <PageNavigationItem
                                                key={item.label}
                                                href={item.href}
                                                isActive={item.isActive}
                                                icon={
                                                    <Icon
                                                        size={24}
                                                        color="white"
                                                    />
                                                }
                                            >
                                                {item.label}
                                            </PageNavigationItem>
                                        )
                                    })}
                                </PageNavigation>
                            </Visibility>
                        </Container>
                    </GridItem>
                    {children}
                </Grid>
            </DashboardLayoutContainer>
        </DashboardLayoutBreadcrumbContextProvider>
    )
}

DashboardLayoutNested.messages = [
    ...useDashboardNavigation.messages,
    ...DashboardLayoutBreadcrumbContextProvider.messages,
    ...DashboardLayoutContainer.messages,
] as const

DashboardLayoutNested.cookies = [...useDashboardNavigation.cookies] as const

DashboardLayoutNested.queryOptions = {
    ...DashboardLayoutContainer.queryOptions,
}
